import { get, onValue, ref } from 'firebase/database'
import { isEmpty } from 'lodash-es'
import { useCallback, useEffect } from 'react'
import { firebaseApi } from '@/api/firebase/firebase-api'
import LocalStorageKeys from '@/constants/localstorage-keys'
import { useAuth } from '@/hooks/use-auth'
import { ampli } from '@/utils/ampli'
import { isClient } from '@/utils/is-client'
import logger from '@/utils/logger'

type UseSingleLogin = (
  triggerState: boolean,
  trigger: React.Dispatch<React.SetStateAction<boolean>>,
) => void

const getLoginTime = (): string | null => {
  return isClient() ? localStorage.getItem(LocalStorageKeys.LoginTime) : null
}

/**
 * @description: single login detector
 */
const useSingleLogin: UseSingleLogin = (triggerState, trigger) => {
  const { isAd, userInfo } = useAuth()
  const path = userInfo ? `/login/${userInfo.id}` : null
  const withPermission = isAd && !userInfo?.incompleteRegistration

  const handleTrigger = useCallback(
    (path: string, newLoginTime?: string): void => {
      const previousLoginTime = getLoginTime()

      if (isEmpty(newLoginTime) || !previousLoginTime) {
        return
      }

      if (previousLoginTime !== newLoginTime) {
        ampli.singleLoginAlert({
          lastLoginTime: previousLoginTime,
          newestLoginTime: newLoginTime ?? '',
          firebaseRef: path,
        })
        trigger(true)
      } else if (triggerState) {
        trigger(false)
      }
    },
    [trigger, triggerState],
  )

  /**
   * 監聽 firebase 的 loginTime，當在不同裝置登入時，會 trigger alert modal
   */
  useEffect(() => {
    if (!path || !withPermission) {
      return
    }

    const unsubscribe = onValue(
      ref(firebaseApi.database(), path),
      (snapshot) => {
        const newLoginTime = snapshot.val()?.toString()
        handleTrigger(path, newLoginTime)
      },
    )

    return unsubscribe
  }, [withPermission, handleTrigger, path])

  /**
   * 監聽 window 的 storage 事件，同步 tabs 狀態
   * e.g. 當 alert modal 開啟時，在某個 tab 重新登入，此時會同步 closed 所有 tabs 的 alert modal
   */
  useEffect(() => {
    if (!path || !withPermission) {
      return
    }
    const handleStorageChanged = (e: StorageEvent): void => {
      if (e.key !== LocalStorageKeys.LoginTime) {
        return
      }

      get(ref(firebaseApi.database(), path))
        .then((snapshot) => {
          const newLoginTime = snapshot.val()?.toString()
          handleTrigger(path, newLoginTime)
        })
        .catch((error) => {
          logger.error('fetching firebaseApi login time failed:', error)
        })
    }

    window.addEventListener('storage', handleStorageChanged)
    return () => {
      window.removeEventListener('storage', handleStorageChanged)
    }
  }, [withPermission, handleTrigger, path])
}

export default useSingleLogin
